import { useEffect, useState, useCallback, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { getDashboard, getMe } from '../api/client'
import { DashboardResponse, MeResponse, ApiError } from '../api/types'
import { config } from '../config'

/**
 * Discriminated union representing the possible states of the dashboard view.
 * The consumer just switches on `state` to render the right thing.
 */
export type DashboardState =
  | { state: 'loading' }
  | { state: 'not_linked'; me: MeResponse }
  | { state: 'error'; error: ApiError }
  | { state: 'ready'; data: DashboardResponse; lastFetchedAt: Date; me: MeResponse }

/**
 * Fetches /me and /dashboard on mount, and refreshes at a fixed interval.
 * Manual refresh available via the returned `refresh` function.
 */
export function useDashboard() {
  const { instance } = useMsal()
  const [result, setResult] = useState<DashboardState>({ state: 'loading' })
  // Track "in flight" so a manual refresh mid-auto-refresh doesn't clash
  const inflight = useRef(false)

  const fetchData = useCallback(async () => {
    if (inflight.current) return
    inflight.current = true
    try {
      const me = await getMe(instance)
      if (!me.is_linked) {
        setResult({ state: 'not_linked', me })
        return
      }

      const data = await getDashboard(instance)
      setResult({ state: 'ready', data, lastFetchedAt: new Date(), me })
    } catch (err) {
      const apiError = err as ApiError
      // 403 not_linked shouldn't reach here (me endpoint catches it first)
      // but handle defensively in case of race conditions on identity claim
      if (apiError.status === 403 && apiError.code === 'not_linked') {
        setResult({
          state: 'not_linked',
          me: { sub: '', email: null, customer_id: null, is_linked: false },
        })
        return
      }
      setResult({ state: 'error', error: apiError })
    } finally {
      inflight.current = false
    }
  }, [instance])

  useEffect(() => {
    fetchData()
    const timer = setInterval(fetchData, config.refreshIntervalMs)
    return () => clearInterval(timer)
  }, [fetchData])

  return { result, refresh: fetchData }
}
